import styled, { css, keyframes } from 'styled-components';
import { SizesType, StatusButtonType } from '../ThemeProvider/types';
import { ButtonHierarchy, ButtonWidth } from './types';

const spin = keyframes`
from {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
`; // TODO: Mejorar, chequear si se está usando y si están bien los valores.

const ButtonFlex = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const ButtonContainer = styled.div`
  position: relative;
  ${ButtonFlex}
`;

export const ButtonText = styled.p`
  margin: 0;
`;

export const Spinner = styled.div<{
  size: SizesType;
  status: StatusButtonType;
  hierarchy: ButtonHierarchy;
}>`
  display: block;
  border: 2px solid;
  border-radius: 50%;
  -webkit-animation: ${spin} 1s infinite linear;
  animation: ${spin} 1s infinite linear;
  ${({ theme, size, hierarchy, status }) => {
    let borderColor;
    let borderTopColor;

    if (hierarchy === 'primary') {
      borderColor = 'rgba(255, 255, 255, 0.3)';
      borderTopColor = theme.white;
    } else if (hierarchy === 'secondary') {
      borderColor = `${theme.status[status].default}40`;
      borderTopColor = theme.status[status].default;
    } else {
      // minimal
      borderColor = `${theme.button.defaultLinkColor}40`;
      borderTopColor = theme.button.defaultLinkColor;
    }

    return css`
      width: ${theme.button.sizes[size].spinner}px;
      height: ${theme.button.sizes[size].spinner}px;
      border-color: ${borderColor};
      border-top-color: ${borderTopColor};
      opacity: 0.9;
    `;
  }}
`;

export const SubmittingContainer = styled.div<{
  loaderWidth: number;
  status: StatusButtonType;
  hasIcon: boolean;
}>`
  position: absolute;
  left: -2px;
  top: -2px;
  height: 100%;
  width: ${({ hasIcon }) => (hasIcon ? 'calc(100% - 4px)' : '100%')};
  border-radius: 50px;
  border-width: 2px;
  border-style: solid;
  ${ButtonFlex}
  ${({ theme, status }) => css`
    border-color: ${theme.status[status].background};
    background-color: ${theme.status[status].background};
    color: ${theme.status[status].contrast};
  `};

  transition: clip-path 800ms linear, background-color 600ms linear;
  clip-path: ${({ loaderWidth }) => `inset(0 ${100 - loaderWidth}% 0 0)`};
`;

export const Button = styled.button<{
  status: StatusButtonType;
  hierarchy: ButtonHierarchy;
  size: SizesType;
  isSubmitting: boolean;
  hasIcon: boolean;
  circular?: boolean;
  width?: ButtonWidth;
  inverted?: boolean;
  isIconButton?: boolean;
}>`
  position: relative;
  border: none;
  font-family: 'Mulish', sans-serif;
  font-weight: bold;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  border-radius: 8px;
  width: ${({ width, isIconButton, theme, size }) =>
    isIconButton ? `${theme.button.sizes[size].height}` : `${width}px`};
  padding: 0;
  cursor: ${({ isSubmitting }) => (isSubmitting ? 'auto' : 'pointer')};
  outline: none;

  /* Centrar el contenido cuando está en estado de submitting */
  ${({ isSubmitting }) =>
    isSubmitting &&
    css`
      display: flex;
      align-items: center;
      justify-content: center;
    `}

  ${({ theme: { button }, size }) => `
    font-size: ${button.sizes[size].fontSize}px;
    line-height: ${button.sizes[size].lineHeight}px;
    height: ${button.sizes[size].height}px;
  `}

  ${({ theme: { button }, size, circular }) =>
    circular
      ? `
    padding-left: ${button.sizes[size].circularPadding}px;
    padding-right: ${button.sizes[size].circularPadding}px;
  `
      : `padding-left: ${button.sizes[size].padding}px;
    padding-right: ${button.sizes[size].padding}px;`}

  ${({ hasIcon }) =>
    hasIcon &&
    css`
      & > ${SubmittingContainer} {
        padding-right: 4px;
      }
    `}

  & > ${ButtonContainer},
  & > ${SubmittingContainer} {
    flex-direction: 'row';
  }

  & > ${ButtonContainer} > div {
    cursor: ${({ isSubmitting }) => (isSubmitting ? 'auto' : 'pointer')};
  }

  &:disabled {
    cursor: auto;
    & > ${ButtonContainer} > div {
      cursor: auto;
    }
  }

  & > div svg > path,
  & > div svg > rect,
  & > div svg > circle {
    stroke-width: 2px;
  }

  ${({ inverted, status, hierarchy, theme, isSubmitting }) => {
    switch (hierarchy) {
      case 'primary':
        return css`
          transition: box-shadow 0.5s ease, border-color 0.5s ease;
          background-color: ${inverted ? theme.white : theme.status[status].default};
          color: ${inverted ? theme.status[status].default : theme.white};
          & svg path:not([class$='not-color']),
          & svg circle:not([class$='not-color']),
          & svg rect:not([class$='not-color']) {
            fill: ${inverted ? theme.status[status].default : theme.white};
          }
          ${!isSubmitting &&
          css`
            &:hover {
              background-color: ${inverted ? theme.white : theme.status[status].hover};
              box-shadow: ${inverted ? `0px 4px 4px 0px ${theme.blackTrans}` : 'none'};
            }
            @media (pointer: coarse) {
              &:hover {
                background-color: ${inverted ? theme.white : theme.status[status].default};
              }
            }
            &:active {
              background-color: ${inverted ? theme.white : theme.status[status].active};
              color: ${inverted
                ? `${theme.status[status].active}`
                : `${theme.status[status].background}`};
              box-shadow: ${inverted && 'none'};
              & svg path:not([class$='not-color']),
              & svg circle:not([class$='not-color']),
              & svg rect:not([class$='not-color']) {
                fill: ${inverted
                  ? `${theme.status[status].active}`
                  : `${theme.status[status].background}`};
              }
            }
          `}
          &:disabled {
            background-color: ${!inverted
              ? `${theme.grayCC}  `
              : `${theme.status[status].background}  `};
            opacity: ${!inverted ? 1 : 0.5};
            color: ${!inverted ? `${theme.white}` : `${theme.grayB8}`};
            pointer-events: none;
            & > ${ButtonContainer} * svg path:not([class$='not-color']),
            & > ${ButtonContainer} * svg circle:not([class$='not-color']),
            & > ${ButtonContainer} * svg rect:not([class$='not-color']) {
              fill: ${!inverted ? `${theme.white}` : `${theme.grayB8}`};
            }
          }
          &:focus-visible {
            padding: 4px;
            outline: none;
            border: solid 2px ${inverted ? theme.status[status].default : `${theme.white}`};
            box-shadow: 0 0 0 2px ${inverted ? `${theme.white}` : `${theme.status[status].active}`};
            color: ${!inverted ? `${theme.white}` : `${theme.status[status].active}`};
            background-color: ${inverted ? `${theme.white}` : `${theme.status[status].default}`};
            box-shadow: 'none';
          }
        `;
      case 'secondary':
        return css`
          background-color: transparent;
          color: ${inverted ? theme.white : theme.status[status].default};
          & svg path:not([class$='not-color']),
          & svg circle:not([class$='not-color']),
          & svg rect:not([class$='not-color']) {
            fill: ${inverted ? theme.white : theme.status[status].default};
          }
          ${!isSubmitting &&
          css`
            &:hover {
              color: ${theme.status[status].hover};
              background-color: ${theme.white};
              box-shadow: ${!inverted ? 'none' : `0px 4px 4px 0px ${theme.blackTrans}`};
              & svg path:not([class$='not-color']),
              & svg circle:not([class$='not-color']),
              & svg rect:not([class$='not-color']) {
                fill: ${theme.status[status].hover};
              }
            }

            @media (pointer: coarse) {
              &:hover {
                background-color: transparent;
              }
            }

            &:active {
              color: ${theme.status[status].active};
              background-color: ${!inverted ? theme.white : `${theme.status[status].opacity}`};
              box-shadow: none;
              & svg path:not([class$='not-color']),
              & svg circle:not([class$='not-color']),
              & svg rect:not([class$='not-color']) {
                fill: ${theme.status[status].active};
              }
            }
          `}
          &:disabled {
            color: ${theme.grayB8};
            background-color: ${theme.white};
            opacity: 0.5;
            & > ${ButtonContainer} * svg path:not([class$='not-color']),
            & > ${ButtonContainer} * svg circle:not([class$='not-color']),
            & > ${ButtonContainer} * svg rect:not([class$='not-color']) {
              fill: ${theme.grayB8};
            }
          }
          &:focus-visible {
            padding: 4px;
            outline: none;
            border: solid 2px
              ${inverted ? `${theme.status[status].default}` : theme.status[status].active};
            box-shadow: 0 0 0 2px ${theme.white};
            color: ${inverted ? theme.white : theme.status[status].default};
            box-shadow: 'none';
            & svg path:not([class$='not-color']),
            & svg circle:not([class$='not-color']),
            & svg rect:not([class$='not-color']) {
              fill: ${inverted ? theme.white : theme.status[status].default};
            }
          }
        `;
      case 'minimal':
        return css`
          background-color: transparent;
          border: none;
          padding: 0;
          width: fit-content;
          height: fit-content;
          color: ${theme.button.defaultLinkColor};
          & svg path:not([class$='not-color']),
          & svg circle:not([class$='not-color']),
          & svg rect:not([class$='not-color']) {
            fill: ${theme.button.defaultLinkColor};
          }
          ${!isSubmitting &&
          css`
            p:hover {
              border-bottom: 1px solid ${theme.button.hoverLinkColor};
              color: ${theme.button.hoverLinkColor};
              & svg path:not([class$='not-color']),
              & svg circle:not([class$='not-color']),
              & svg rect:not([class$='not-color']) {
                fill: ${theme.button.hoverLinkColor};
              }
            }
            @media (pointer: coarse) {
              p:hover {
                color: ${theme.button.defaultLinkColor};
              }
            }
          `}

          &:active {
            color: ${theme.button.activeLinkColor};
            & svg path:not([class$='not-color']),
            & svg circle:not([class$='not-color']),
            & svg rect:not([class$='not-color']) {
              fill: ${theme.button.activeLinkColor};
            }
          }

          &:disabled {
            color: ${theme.grayB8};
            & > ${ButtonContainer} * svg path:not([class$='not-color']),
            & > ${ButtonContainer} * svg circle:not([class$='not-color']),
            & > ${ButtonContainer} * svg rect:not([class$='not-color']) {
              fill: ${theme.grayB8};
            }
            p:hover {
              border: none;
              color: ${theme.grayB8};
            }
            @media (pointer: coarse) {
              p:hover {
                color: ${theme.grayB8};
              }
            }
          }
          &:focus-visible {
            text-decoration: underline;
          }
        `;
      default:
        return css``;
    }
  }}
`;
